// Is a creative finished enough to traffic? Drives the green check on the
// asset card during the naming / review-URL step.
//
// This used to live inline in HorizontalAssetPreview, gated on the route
// pathname, and only ever learned about /display and /preloader. Every other
// route — /smart, /video, /audio, /html5, /staticimage, /default — showed no
// check at all no matter how complete the creative was, which is what made
// existing video creatives look unfinished when they were ready on arrival.
//
// Keyed on the creative rather than the route, because the requirement is a
// property of the creative: a video needs a name, a display banner needs a
// name and a landing page, wherever it was uploaded from.

import { getCreativeType, CREATIVE_TYPE } from "./creativeMatching";

export const RICH_MEDIA_TYPES = [
  "RICH_MEDIA_DISPLAY_BANNER",
  "RICH_MEDIA_DISPLAY_EXPANDING",
  "RICH_MEDIA_DISPLAY_INTERSTITIAL"
];

const READY = { isReady: true, reason: null };

/**
 * Whether a creative needs a click-through URL of its own.
 *
 * Video and audio do not: CM360 documents Creative.clickTags as applicable to
 * DISPLAY_IMAGE_GALLERY, FLASH_INPAGE, HTML5_BANNER and DISPLAY only, and an
 * in-stream ad carries its click-through on the ad. Requiring a landing page
 * from them is what kept the check off a perfectly good video.
 */
export function needsLandingPage(creative) {
  const dcm = creative && creative.dcmCreative;

  // Tracking creatives have no destination of their own.
  if (dcm && typeof dcm.type === "string" && dcm.type.includes("TRACKING")) {
    return false;
  }

  return getCreativeType(creative) === CREATIVE_TYPE.DISPLAY;
}

/**
 * @param {object} creative - a creative from state.creatives.byId
 * @returns {{ isReady: boolean, reason: string|null }}
 */
export function getCreativeReadiness(creative) {
  if (!creative) return { isReady: false, reason: "Missing creative." };

  // Backups inherit their primary's taxonomy when the primary is saved.
  if (creative.creativeRole === "BACKUP") return READY;

  // Existing GCM companions already carry their name from Campaign Manager.
  if (
    creative.creativeRole === "COMPANION" &&
    creative.file &&
    creative.file.id
  ) {
    return READY;
  }

  const dcm = creative.dcmCreative;

  // Rich media carries its click-through inside the creative itself, so it is
  // ready the moment it is picked — there is nothing for the user to fill in.
  if (dcm && RICH_MEDIA_TYPES.includes(dcm.type)) return READY;

  if (!creative.taxonomy || !creative.taxonomy.name) {
    const label =
      creative.creativeRole === "COMPANION" ? "companion" : "primary";
    return {
      isReady: false,
      reason: `You need to add naming for each ${label} asset.`
    };
  }

  if (needsLandingPage(creative)) {
    const clickTags = (dcm && dcm.clickTags) || null;
    if (clickTags && clickTags.length && !clickTags[0].clickThroughUrl) {
      return {
        isReady: false,
        reason: "You need to set click through url for each asset."
      };
    }

    if (!creative.taxonomy.urlId) {
      return {
        isReady: false,
        reason: "You need to set click through url for each asset."
      };
    }
  }

  return READY;
}

export function isCreativeReady(creative) {
  return getCreativeReadiness(creative).isReady;
}
